import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EncodeDecode from '../../../components/EncodeDecode';
import API from '../../../api/Api';
import imgNotFound from '../../../assets/image-not-found.png';
import config from '../../../constants/config';
import Cart from './Cart';
import CallStaffButton from './CallStaffButton';
import OfflineBanner from '../../../components/OfflineBanner';
import { getVertical } from '../../../constants/verticals';
import { useLanguage } from '../../../contexts/LanguageContext';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { venueLangCodes } from '../../../i18n/languages';
import localized, { localizedDesc } from '../../../utils/localized';
import './menu.css';

// ─── Modifier selection sheet ──────────────────────────────────────────────────
// Rendered as a bottom-sheet overlay when a dish has modifier groups.
// Handles single (radio) and multi (checkbox) groups, enforces min/max, shows
// a live running price, and calls onConfirm with the selected options array.
const ModifierSheet = ({ dish, groups, currencySymbol, onConfirm, onCancel }) => {
    const { lang } = useLanguage();
    const { t } = useTranslation();
    // selected: { [group_id]: Set of option_ids }
    const [selected, setSelected] = useState(() => {
        const init = {};
        groups.forEach(g => { init[g.group.id] = new Set(); });
        return init;
    });

    const basePrice = parseFloat(dish.price || 0);

    // Sum of all selected price_deltas across all groups
    const deltaTotal = groups.reduce((sum, g) => {
        return sum + g.options
            .filter(o => selected[g.group.id] && selected[g.group.id].has(o.id))
            .reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
    }, 0);

    const livePrice = basePrice + deltaTotal;

    const toggleOption = (group, option) => {
        setSelected(prev => {
            const groupSet = new Set(prev[group.id]);
            if (group.selection_type === 'single') {
                // Radio: replace selection
                // For required single groups, always select the tapped option (no deselect allowed)
                if (group.min_select > 0) {
                    groupSet.clear();
                    groupSet.add(option.id);
                } else {
                    // Optional single group: tap to select or deselect if already selected
                    groupSet.clear();
                    if (!prev[group.id].has(option.id)) groupSet.add(option.id);
                }
            } else {
                // Checkbox: toggle, respect max_select
                if (groupSet.has(option.id)) {
                    groupSet.delete(option.id);
                } else {
                    const max = group.max_select > 0 ? group.max_select : Infinity;
                    if (groupSet.size < max) groupSet.add(option.id);
                }
            }
            return { ...prev, [group.id]: groupSet };
        });
    };

    // Validate: all required groups (min_select > 0) have enough selections
    const errors = [];
    groups.forEach(g => {
        const count = selected[g.group.id] ? selected[g.group.id].size : 0;
        if (g.group.min_select > 0 && count < g.group.min_select) {
            const key = g.group.min_select > 1 ? 'menu.modifier_min_error_plural' : 'menu.modifier_min_error';
            errors.push(t(key, { group: localized(g.group, lang), min: g.group.min_select }));
        }
    });

    const handleConfirm = () => {
        if (errors.length > 0) return; // button is disabled when errors exist
        // Build flat list of selected modifier details
        const selectedModifiers = [];
        groups.forEach(g => {
            g.options.forEach(o => {
                if (selected[g.group.id] && selected[g.group.id].has(o.id)) {
                    selectedModifiers.push({
                        group_id: g.group.id,
                        option_id: o.id,
                        name: localized(o, lang),
                        price_delta: Number(o.price_delta) || 0,
                    });
                }
            });
        });
        onConfirm(selectedModifiers, livePrice);
    };

    return (
        <div className='mb-mod-overlay' role='dialog' aria-modal='true' aria-label={t('menu.modifier_customize', { name: localized(dish, lang) })} onClick={onCancel}>
            <div className='mb-mod-sheet' onClick={e => e.stopPropagation()}>
                <div className='mb-mod-header'>
                    <span className='mb-mod-title'>{localized(dish, lang)}</span>
                    <button className='mb-mod-close' aria-label={t('common.close')} onClick={onCancel}>×</button>
                </div>
                <div className='mb-mod-body'>
                    {groups.map(({ group, options }) => (
                        <div key={group.id} className='mb-mod-group'>
                            <div className='mb-mod-group-head'>
                                <span className='mb-mod-group-name'>{localized(group, lang)}</span>
                                {group.min_select > 0 && (
                                    <span className='mb-mod-required'>{t('menu.modifier_required')}</span>
                                )}
                                {group.selection_type === 'multi' && group.max_select > 0 && (
                                    <span className='mb-mod-hint'>
                                        {group.min_select > 0 ? `${group.min_select}–${group.max_select}` : t('menu.modifier_up_to', { max: group.max_select })}
                                    </span>
                                )}
                            </div>
                            {options.map(option => {
                                const isSelected = selected[group.id] && selected[group.id].has(option.id);
                                const atMax = group.selection_type === 'multi' && group.max_select > 0 &&
                                    selected[group.id] && selected[group.id].size >= group.max_select && !isSelected;
                                return (
                                    <button
                                        key={option.id}
                                        type='button'
                                        className={`mb-mod-option${isSelected ? ' selected' : ''}${atMax ? ' disabled' : ''}`}
                                        onClick={() => !atMax && toggleOption(group, option)}
                                        aria-pressed={isSelected}
                                        disabled={atMax}
                                    >
                                        <span className='mb-mod-option-indicator'>
                                            {group.selection_type === 'single'
                                                ? (isSelected ? '●' : '○')
                                                : (isSelected ? '☑' : '☐')}
                                        </span>
                                        <span className='mb-mod-option-name'>{localized(option, lang)}</span>
                                        {Number(option.price_delta) !== 0 && (
                                            <span className='mb-mod-option-delta'>
                                                {Number(option.price_delta) > 0 ? '+' : ''}{currencySymbol}{Number(option.price_delta).toFixed(2)}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div className='mb-mod-footer'>
                    <div className='mb-mod-live-price'>
                        {currencySymbol}{livePrice.toFixed(2)}
                    </div>
                    <button
                        className='mb-mod-confirm'
                        onClick={handleConfirm}
                        disabled={errors.length > 0}
                    >
                        {t('menu.modifier_add_to_order')}
                    </button>
                    {errors.length > 0 && (
                        <p className='mb-mod-error'>{errors[0]}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// An item's attribute tags are stored comma-separated in `dietary_tags`; the available set + display
// labels come from the venue's vertical (constants/verticals.js). dishTags() parses the stored keys.
const dishTags = (item) => (item.dietary_tags || '')
    .split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

// Dynamic per-venue branding block (name, slogan, optional logo) — all from the DB.
// Defined at module scope (not inside MenuPage) so React doesn't remount it on every render.
const VenueBrand = ({ venue }) => (
    <div className='mb-brand'>
        {venue?.logo_img && (
            <img src={venue.logo_img} alt={venue.title} className='mb-brand-logo'
                onError={e => { e.target.style.display = 'none'; }} />
        )}
        <h1 className='mb-brand-name'>{venue?.title || 'Menu'}</h1>
        {venue?.slogan && <p className='mb-brand-slogan'>{venue.slogan}</p>}
        <div className='mb-brand-switcher'><LanguageSwitcher allowed={venueLangCodes(venue?.diner_languages)} /></div>
        <div className='mb-brand-rule' />
    </div>
);

const MenuPage = () => {
    const { token } = useParams();
    const { lang, setLang } = useLanguage();
    const { t } = useTranslation();
    // Table-QR mode: the table is baked into the scanned link as ?table=N. When present we pre-fill
    // and lock the table in the cart (the guest never types it). Absent → the guest types it (ask-table mode).
    const lockedTable = (() => {
        try {
            const t = new URLSearchParams(window.location.search).get('table');
            return t ? String(t).trim().slice(0, 20) : '';
        } catch (e) { return ''; }
    })();
    const [venueId, setVenueId] = useState(null);
    const [menuId, setMenuId] = useState(null);
    const [venue, setVenue] = useState(null); // dynamic per-venue branding (name, slogan, logo)
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [items, setItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCart, setShowCart] = useState(false);
    // Modifier sheet state
    const [modSheet, setModSheet] = useState(null); // null | { dish, groups }
    const [modLoading, setModLoading] = useState(null); // null | dish.id of the item being fetched; scope loading state to the tapped dish
    const [query, setQuery] = useState('');           // whole-menu search text
    const [diet, setDiet] = useState(null);           // active dietary quick-filter key (veg/vegan/gf/spicy)
    const [allItems, setAllItems] = useState(null);   // null until prefetched across every category
    const [searching, setSearching] = useState(false); // true while the one-time prefetch runs

    // Resolve token → venueId + menuId SERVER-SIDE (so a rotated/revoked QR is honoured). The
    // server falls back to the legacy deterministic decode for any already-printed QR, and returns
    // status:false (with a friendly message) when a token has been replaced. We only fall back to
    // client-side decode on a genuine network error, so a brief API hiccup never breaks a live menu.
    useEffect(() => {
        if (!token) { setError(t('common.menu_unavailable')); setLoading(false); return; }
        let cancelled = false;
        API.getData('menu-token', { token })
            .then(res => {
                if (cancelled) return;
                const d = res.data;
                if (d?.status && d.data?.venue_id) {
                    setVenueId(parseInt(d.data.venue_id));
                    setMenuId(parseInt(d.data.menu_id));
                } else {
                    setError(d?.message || t('common.menu_unavailable'));
                    setLoading(false);
                }
            })
            .catch(() => {
                if (cancelled) return;
                // Network failure — fall back to the legacy client-side decode so non-rotated menus still load.
                const parts = EncodeDecode.getValue(token);
                if (parts && parts.length >= 2 && parseInt(parts[0])) {
                    setVenueId(parseInt(parts[0]));
                    setMenuId(parseInt(parts[1]));
                } else {
                    setError(t('common.menu_unavailable'));
                    setLoading(false);
                }
            });
        return () => { cancelled = true; };
    }, [token]);

    // Fetch the venue's own branding (name, slogan, logo) so each menu feels like that restaurant.
    useEffect(() => {
        if (!venueId) return;
        API.getData(`venues/${venueId}`)
            .then(res => { if (res.data?.status) setVenue(res.data.data ?? null); })
            .catch(() => {});
    }, [venueId]);

    // Honour the venue's chosen diner languages: if the active language isn't one this venue offers,
    // snap to the venue's primary. (Unset venues keep the legacy en/th/ru behaviour.)
    useEffect(() => {
        if (!venue) return;
        const allowed = venueLangCodes(venue.diner_languages);
        if (!allowed.includes(lang)) setLang(allowed[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venue]);

    // Fetch categories once we have a venueId. (menuId may be 0 — still fetch and show
    // a graceful "menu not ready" state rather than spinning forever.)
    useEffect(() => {
        if (venueId === null) return; // wait until token is decoded
        if (!venueId) { setError(t('common.menu_unavailable')); setLoading(false); return; }
        setLoading(true);
        API.getData('venue-category/venue', { venue_id: venueId, menu_id: menuId || 0 })
            .then(res => {
                const data = res.data;
                if (data?.status && data.data?.length) {
                    setCategories(data.data);
                    setActiveCategory(data.data[0].id);
                } else {
                    setCategories([]);
                }
            })
            .catch(() => setError(t('common.menu_unavailable')))
            .finally(() => setLoading(false));
    }, [venueId, menuId]);

    // Fetch items when category changes
    useEffect(() => {
        if (!activeCategory) return;
        API.getData(`venue-subcategory/category/${activeCategory}`)
            .then(res => {
                const data = res.data;
                if (data?.status && data.data?.length) {
                    setItems(data.data.filter(i => !i.is_deleted && i.is_active));
                } else {
                    setItems([]);
                }
            })
            .catch(() => setItems([]));
    }, [activeCategory]);

    // Lazily fetch EVERY category's items the first time the guest searches OR picks a dietary
    // filter, so both span the whole menu (not just the active category). Categories are already
    // menu-scoped, so this reuses the same public endpoint per category — no backend change. Runs
    // at most once per menu load.
    useEffect(() => {
        if ((!query.trim() && !diet) || allItems !== null || searching || categories.length === 0) return;
        setSearching(true);
        Promise.all(categories.map(cat =>
            API.getData(`venue-subcategory/category/${cat.id}`)
                .then(res => (res.data?.status && res.data.data?.length)
                    ? res.data.data
                        .filter(i => !i.is_deleted && i.is_active)
                        .map(i => ({ ...i, _cat: cat }))
                    : [])
                .catch(() => [])
        ))
            .then(lists => {
                // Flatten + dedupe by id (an item lives in one category, but stay safe).
                const seen = new Set();
                const flat = [];
                lists.forEach(list => list.forEach(i => {
                    if (seen.has(i.id)) return;
                    seen.add(i.id);
                    flat.push(i);
                }));
                setAllItems(flat);
            })
            .finally(() => setSearching(false));
    }, [query, diet, allItems, searching, categories]);

    // The flat result list for the current search text AND/OR dietary filter. A query matches the
    // dish name or description; the diet filter keeps only dishes carrying that tag. Both compose.
    const q = query.trim().toLowerCase();
    const filterActive = q.length > 0 || !!diet;
    // Recompute only when the search text, diet filter, or the underlying item set changes —
    // not on every unrelated re-render (cart updates, modal open/close, etc.).
    const filteredResults = useMemo(() => (
        filterActive && allItems
            ? allItems.filter(i => {
                const name = (localized(i, lang) + ' ' + (i.lang_en || '')).toLowerCase();
                return (!q || name.includes(q) || (i.description || '').toLowerCase().includes(q)) &&
                    (!diet || dishTags(i).includes(diet));
            })
            : []
    ), [filterActive, q, diet, allItems, lang]);
    // Human label describing the active filter, for the result meta + empty state.
    const filterLabel = [q && `“${query.trim()}”`, diet && tagMeta(diet)?.label].filter(Boolean).join(' · ');

    // Compute the dedup fingerprint for a cart line: dish id + sorted selected option ids.
    // Same dish with same options → increment qty; same dish with different options → new line.
    const cartFingerprint = (dishId, selectedModifiers) => {
        const optionIds = (selectedModifiers || []).map(m => m.option_id).sort((a, b) => a - b);
        return `${dishId}|${optionIds.join(',')}`;
    };

    // Adds a dish to cart, with optional selectedModifiers and effective unit price.
    // Called directly when the dish has no modifier groups (original behaviour),
    // or by the ModifierSheet confirm handler (with modifier data).
    const commitToCart = useCallback((item, selectedModifiers, effectivePrice) => {
        const mods = selectedModifiers || [];
        const unitPrice = effectivePrice != null ? effectivePrice : parseFloat(item.price || 0);
        const key = cartFingerprint(item.id, mods);
        setCart(prev => {
            const existing = prev.find(c => c._key === key);
            if (existing) {
                return prev.map(c => c._key === key ? { ...c, qty: c.qty + 1 } : c);
            }
            return [...prev, { ...item, qty: 1, price: unitPrice, selectedModifiers: mods, _key: key }];
        });
    }, []);

    // "Add +" button handler — fetches modifier groups; if none, adds directly.
    const addToCart = useCallback((item) => {
        setModLoading(item.id);
        API.getData(`modifier/for-dish/${item.id}`)
            .then(res => {
                const groups = (res.data?.status && Array.isArray(res.data?.data) && res.data.data.length > 0)
                    ? res.data.data
                    : null;
                if (groups) {
                    setModSheet({ dish: item, groups });
                } else {
                    commitToCart(item, [], parseFloat(item.price || 0));
                }
            })
            .catch(() => {
                // Network error — add without modifiers so the guest isn't blocked
                commitToCart(item, [], parseFloat(item.price || 0));
            })
            .finally(() => setModLoading(null));
    }, [commitToCart]);

    const cartTotal = cart.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 0), 0);
    const cartCount = cart.reduce((sum, i) => sum + (parseInt(i.qty) || 0), 0);

    // --- Cart persistence across reloads -------------------------------------------------
    // The cart lives in React state, so a refresh / accidental tab-close / phone lock would wipe a
    // half-built order. We mirror it into localStorage keyed by this venue+menu, hydrate it once the
    // QR token resolves to a venueId+menuId, and keep it in sync on every change. A 12h TTL stops a
    // stale cart from resurfacing days later; an emptied cart (after a placed order) clears storage.
    const cartStorageKey = (venueId && menuId) ? `mb_cart_${venueId}_${menuId}` : null;
    const cartHydrated = useRef(false);

    useEffect(() => {
        if (!cartStorageKey) return;
        try {
            const raw = localStorage.getItem(cartStorageKey);
            if (raw) {
                const saved = JSON.parse(raw);
                const fresh = saved && Array.isArray(saved.items) &&
                    (Date.now() - (saved.savedAt || 0) < 12 * 60 * 60 * 1000);
                if (fresh && saved.items.length) setCart(saved.items);
            }
        } catch (e) { /* corrupt/blocked storage — ignore and start with an empty cart */ }
        cartHydrated.current = true;
    }, [cartStorageKey]);

    useEffect(() => {
        // Don't write until the initial hydration has run, or the first empty render would clobber
        // a saved cart before we've had a chance to restore it.
        if (!cartStorageKey || !cartHydrated.current) return;
        try {
            if (cart.length) {
                localStorage.setItem(cartStorageKey, JSON.stringify({ items: cart, savedAt: Date.now() }));
            } else {
                localStorage.removeItem(cartStorageKey);
            }
        } catch (e) { /* storage full or blocked (private mode) — non-fatal, cart still works in-memory */ }
    }, [cart, cartStorageKey]);

    // Venue isn't open for online ordering — either a manual pause (kitchen closed) OR outside the
    // scheduled opening hours. Guests can still browse, but the Add buttons + cart are hidden and a
    // banner explains why. The server also rejects the order (order.service.create) so this is just
    // the friendly front door, not the security boundary. `open_now` is computed server-side; fall
    // back to the manual flag for any older API response that predates it.
    const ordersClosed = !!venue && (
        venue.open_now === false ||
        (venue.open_now === undefined && Number(venue.accepting_orders) === 0)
    );
    // Why we're closed: 'hours' (outside the schedule) vs 'paused' (manual). Drives the banner copy.
    const closedByHours = ordersClosed && venue?.closed_reason === 'hours';
    // The owner's own closed-state message (Settings → Online Ordering → "Closed message"). When set,
    // it speaks for the venue ("Closed for a private event — back tomorrow") instead of our generic copy.
    const closedNote = (venue?.closed_note || '').trim();

    // Currency the guest sees on every price. The owner picks it in Settings; the backend returns it
    // on the venue object with a '€' fallback, so older venues that never picked one keep showing €.
    const currencySymbol = venue?.currency_symbol || config.currencySymbol;
    // Wording + attribute-tag vocabulary for this venue's vertical (restaurant / retail / services).
    const V = getVertical(venue?.vertical);
    const tagMeta = (key) => V.tags.find(t => t.key === key);

    if (loading) return (
        <div className='mb-menu container-sm' style={{ maxWidth: 480 }}>
            <div className='mb-state'>
                <div className='spinner-border' role='status' style={{ color: '#d97757' }} />
                <p className='mb-state-sub mt-3'>{t('menu.loading')}</p>
            </div>
        </div>
    );

    if (error) return (
        <div className='mb-menu container-sm' style={{ maxWidth: 480 }}>
            <div className='mb-state'>
                <div style={{ fontSize: 40 }}>🍽️</div>
                <p className='mb-state-title'>{error}</p>
                <p className='mb-state-sub'>{t('menu.error_scan_again')}</p>
            </div>
        </div>
    );

    // One dish card — reused by category browsing AND search results. `catLabel` shows the
    // dish's category as a chip (only useful in flat search results, where tabs are hidden).
    const renderItemCard = (item, catLabel) => (
        <div className='mb-item' key={item.id}>
            <div className='mb-item-img'>
                <img
                    src={(item.image_path && item.image_name) ? `${item.image_path}/${item.image_name}` : imgNotFound}
                    alt={localized(item, lang)}
                    onError={e => { e.target.src = imgNotFound; }}
                />
            </div>
            <div className='mb-item-body'>
                {/* (B6b) Auto social-proof badge — one per dish, from ratings (favourite) or recent orders (popular). */}
                {item.badge && (
                    <span className={`mb-badge mb-badge-${item.badge}`}>
                        {item.badge === 'favourite'
                            ? `⭐ ${t('menu.badge_favourite', 'Customer Favourite')}`
                            : `🔥 ${t('menu.badge_popular', 'Popular')}`}
                    </span>
                )}
                <div className='mb-item-head'>
                    <span className='mb-item-name'>
                        {localized(item, lang)}
                        {item.spice_level > 0 && (
                            <span className='mb-item-spice' title={`Spice ${item.spice_level}/3`} style={{ marginLeft: 6 }}>
                                {'🌶️'.repeat(item.spice_level)}
                            </span>
                        )}
                    </span>
                    <span className='mb-item-price'>
                        {currencySymbol}{parseFloat(item.price || 0).toFixed(2)}
                    </span>
                </div>
                {/* (B6) Aggregate diner rating — social proof, shown only once a dish has ratings. */}
                {item.rating_count > 0 && (
                    <div className='mb-item-rating' title={`${Number(item.avg_rating).toFixed(1)} / 5 · ${item.rating_count} ${item.rating_count === 1 ? 'rating' : 'ratings'}`}>
                        <span className='mb-stars' aria-hidden='true'>
                            {[1, 2, 3, 4, 5].map(n => (
                                <span key={n} className={n <= Math.round(item.avg_rating) ? 'on' : ''}>★</span>
                            ))}
                        </span>
                        <span className='mb-rating-num'>{Number(item.avg_rating).toFixed(1)}</span>
                        <span className='mb-rating-count'>· {item.rating_count}</span>
                    </div>
                )}
                {dishTags(item).length > 0 && (
                    <div className='mb-item-tags'>
                        {dishTags(item).map(k => {
                            const m = tagMeta(k);
                            return m ? (
                                <span key={k} className={`mb-tag mb-tag-${k}`}>
                                    <span aria-hidden='true'>{m.icon}</span> {t(`menu.tag_${k}`) || m.short}
                                </span>
                            ) : null;
                        })}
                    </div>
                )}
                {catLabel && <span className='mb-item-cat'>{catLabel}</span>}
                {localizedDesc(item, lang) && (
                    <p className='mb-item-desc'>{localizedDesc(item, lang)}</p>
                )}
                <div className='mb-item-foot'>
                    {item.is_soldout ? (
                        <span className='mb-soldout'>{t('menu.sold_out')}</span>
                    ) : ordersClosed ? (
                        <span className='mb-closed-pill'>{t('menu.ordering_closed')}</span>
                    ) : (
                        <button className='mb-add-btn' onClick={() => addToCart(item)} disabled={modLoading === item.id}>
                            {t('menu.add')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // Venue exists but has no menu categories yet
    if (categories.length === 0) {
        return (
            <div className='mb-menu container-sm' style={{ maxWidth: 480 }}>
                <VenueBrand venue={venue} />
                <div className='mb-state'>
                    <div style={{ fontSize: 40 }}>🍴</div>
                    <p className='mb-state-title'>{V.nouns.emptyMenu}</p>
                    <p className='mb-state-sub'>{t('menu.not_published', { menu: V.nouns.menu.toLowerCase() })}</p>
                </div>
            </div>
        );
    }

    return (
        <div className='mb-menu container-sm' style={{ maxWidth: 480 }}>
            <OfflineBanner />
            <VenueBrand venue={venue} />
            {ordersClosed ? (
                <div className='mb-closed-banner' role='status'>
                    <span className='mb-closed-icon' aria-hidden='true'>{closedByHours ? '🕒' : '🌙'}</span>
                    <div>
                        <strong>{closedByHours ? t('menu.closed_by_hours_title') : t('menu.closed_by_pause_title')}</strong>
                        <p>
                            {closedNote
                                ? `${closedNote} ${t('menu.closed_note_suffix')}`
                                : closedByHours
                                    ? `${venue?.next_open_label ? venue.next_open_label + '. ' : ''}${t('menu.closed_note_suffix')}`
                                    : `${venue?.title || ''} ${t('menu.closed_paused_prefix')}`}
                        </p>
                    </div>
                </div>
            ) : (
                <p className='mb-greeting'>{t('menu.browse_prompt') || V.nouns.browse}</p>
            )}

            {/* Whole-menu search — filters dishes across every category */}
            <div className='mb-search'>
                <span className='mb-search-icon' aria-hidden='true'>🔍</span>
                <input
                    type='text'
                    className='mb-search-input'
                    placeholder={t('menu.search_placeholder') || V.nouns.search}
                    aria-label={t('menu.search_placeholder') || V.nouns.search}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
                {query && (
                    <button
                        type='button'
                        className='mb-search-clear'
                        aria-label={t('menu.search_clear')}
                        onClick={() => setQuery('')}
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Dietary quick-filters — tap to show only matching dishes across the whole menu */}
            <div className='mb-diet' role='group' aria-label={t('menu.filter_by_attribute')}>
                {V.tags.map(tag => (
                    <button
                        key={tag.key}
                        type='button'
                        className={`mb-diet-pill${diet === tag.key ? ' active' : ''}`}
                        aria-pressed={diet === tag.key}
                        onClick={() => setDiet(diet === tag.key ? null : tag.key)}
                    >
                        <span aria-hidden='true'>{tag.icon}</span> {t(`menu.tag_${tag.key}`) || tag.short}
                    </button>
                ))}
            </div>

            {filterActive ? (
                /* ---- Filter mode: flat results across the whole menu (search and/or diet) ---- */
                searching && !allItems ? (
                    <div className='mb-state'>
                        <div className='spinner-border' role='status' style={{ color: '#d97757' }} />
                        <p className='mb-state-sub mt-3'>{t('menu.searching')}</p>
                    </div>
                ) : filteredResults.length === 0 ? (
                    <p className='mb-empty'>{t('menu.no_items', { items: V.nouns.items, label: filterLabel })}</p>
                ) : (
                    <>
                        <p className='mb-search-meta'>
                            {t(filteredResults.length === 1 ? 'menu.results_count_one' : 'menu.results_count_other', { count: filteredResults.length, label: filterLabel })}
                        </p>
                        <div className='mb-items'>
                            {filteredResults.map(item => renderItemCard(item, localized(item._cat, lang)))}
                        </div>
                    </>
                )
            ) : (
                /* ---- Browse mode: category tabs + that category's items ---- */
                <>
                    {/* Category tabs (sticky pill bar) */}
                    <div className='mb-cats'>
                        {categories.map(cat => (
                            <div
                                key={cat.id}
                                className={`mb-cat${activeCategory === cat.id ? ' active' : ''}`}
                                onClick={() => setActiveCategory(cat.id)}
                            >
                                {localized(cat, lang)}
                            </div>
                        ))}
                    </div>

                    {/* Menu items */}
                    {items.length === 0 ? (
                        <p className='mb-empty'>{t('menu.empty_category') || V.nouns.emptyCategory}</p>
                    ) : (
                        <div className='mb-items'>
                            {items.map(item => renderItemCard(item, null))}
                        </div>
                    )}
                </>
            )}

            {/* Floating cart button — hidden when cart offcanvas is open */}
            {cartCount > 0 && !showCart && !ordersClosed && (
                <div className='mb-cartbar'>
                    <button onClick={() => setShowCart(true)}>
                        <span className='mb-cart-count'>{t('menu.view_cart', { count: cartCount })}</span>
                        <span className='mb-cart-total'>{currencySymbol}{cartTotal.toFixed(2)}</span>
                    </button>
                </div>
            )}

            {/* Plan-gated "Powered by" credit — shown only for Starter (and expired-trial) venues.
                Pro/Business pay to remove it (see /billing "Remove CometQR credit"). Every Starter
                menu thus advertises the product to guests = the freemium growth loop. */}
            {venue?.show_branding && (
                <div className='mb-credit'>
                    {t('common.powered_by')} <a href='/' className='mb-credit-brand'>CometQR</a>
                    <span className='mb-credit-tag'>{t('common.digital_qr_menus')}</span>
                </div>
            )}

            {/* Dine-in "Call staff / Ask for the bill" — floats bottom-left, clear of the cart bar.
                Hidden while the cart offcanvas is open so it can't sit over the cart UI. */}
            {!showCart && <CallStaffButton venueId={venueId} menuId={menuId} />}

            {/* Modifier selection sheet — rendered when a dish with groups is tapped */}
            {modSheet && (
                <ModifierSheet
                    dish={modSheet.dish}
                    groups={modSheet.groups}
                    currencySymbol={currencySymbol}
                    onConfirm={(selectedModifiers, effectivePrice) => {
                        commitToCart(modSheet.dish, selectedModifiers, effectivePrice);
                        setModSheet(null);
                    }}
                    onCancel={() => setModSheet(null)}
                />
            )}

            <Cart
                show={showCart}
                onHide={() => setShowCart(false)}
                cart={cart}
                setCart={setCart}
                venueId={venueId}
                menuId={menuId}
                currencySymbol={currencySymbol}
                lockedTable={lockedTable}
                loyalty={venue && Number(venue.loyalty_enabled) ? { model: venue.loyalty_model, target: Number(venue.loyalty_target), reward: venue.loyalty_reward } : null}
            />
        </div>
    );
};

export default MenuPage;
